import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Banner, Box, Button, Divider, DropdownButtonContext, FlexBox, InputRichText, Skeleton, Text, } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { memo, useCallback, useContext, useMemo, useState } from 'react';
import { analytics } from 'app/utils/segment';
import { AiBanner } from 'components/aiBanner';
import { SafeInnerHtmlSetter } from 'components/shared/util/SafeInnerHtmlSetter';
import { sanitizeHtml } from 'utils/sanitizeHtml';
import { useTranslateText } from 'utils/useTranslateText';
import { CopilotLanguageInputSelectionList, } from './components';
import styles from './copilotDropdownTranslate.module.scss';
// Since we just translate the text, no new tags will be created, so using email works for all channels as it supports the widest range of tags.
const sanitizationProfile = 'email';
const getFragmentString = (fragment) => {
    var _a;
    if (!fragment) {
        return '';
    }
    if (fragment instanceof Text) {
        return (_a = fragment.textContent) !== null && _a !== void 0 ? _a : '';
    }
    if (fragment instanceof Element) {
        return fragment.outerHTML;
    }
    const div = document.createElement('div');
    div.appendChild(fragment);
    return div.innerHTML;
};
/**
 * Represents the component that translates selected text or entire content of
 * the editor
 */
export const CopilotDropdownTranslate = memo(function CopilotDropdownTranslate() {
    const editorContext = useContext(InputRichText.Context);
    const dropdownContext = useContext(DropdownButtonContext);
    const [translate, { asyncStatus, translatedText }] = useTranslateText();
    const [selectedLanguage, setSelectedLanguage] = useState();
    /**
     * Gets selected text. If nothing is selected, returns the full content of
     * the text editor.
     */
    const getSelectedText = useCallback(() => {
        var _a, _b;
        const range = editorContext.selection.getRange();
        if (range && !range.collapsed) {
            return getFragmentString(range.cloneContents());
        }
        return (_b = (_a = editorContext.ref.current) === null || _a === void 0 ? void 0 : _a.innerHTML) !== null && _b !== void 0 ? _b : '';
    }, [editorContext.ref, editorContext.selection]);
    /** Handles the change event of languages selection list. */
    const handleLanguageChange = useCallback((event) => {
        var _a;
        setSelectedLanguage(event.selectedLanguage);
        if (!((_a = event.selectedLanguage) === null || _a === void 0 ? void 0 : _a.name)) {
            return;
        }
        const text = getSelectedText();
        translate({
            language: event.selectedLanguage.name,
            text,
        });
    }, [getSelectedText, translate]);
    /**
     * Recreates editor's selection. If nothing was previously selected, it
     * selects all contents of the editor.
     *
     * @param collapseToEnd Whether to collpase to the end of the selection once
     *   it's created. Useful when inserting content in the end of the
     *   selection, instead of replacing it.
     */
    const createEditorSelection = useCallback((collapseToEnd) => {
        const selection = editorContext.selection.recreate();
        const range = selection === null || selection === void 0 ? void 0 : selection.getRangeAt(0);
        if ((range === null || range === void 0 ? void 0 : range.collapsed) && editorContext.ref.current) {
            range.selectNodeContents(editorContext.ref.current);
        }
        if (collapseToEnd) {
            selection === null || selection === void 0 ? void 0 : selection.collapseToEnd();
        }
        return selection;
    }, [editorContext.ref, editorContext.selection]);
    /**
     * Inserts the translated text under the selection. If nothing is selected,
     * a new line will be added at the end of the editor's content.
     */
    const insertBelow = useCallback(() => {
        const range = editorContext.selection.getRange();
        if (range && translatedText) {
            const div = editorContext.window.document.createElement('div');
            div.appendChild(
            // eslint-disable-next-line no-unsanitized/method
            range.createContextualFragment(sanitizeHtml(translatedText, sanitizationProfile)));
            editorContext.content.insert(div, {
                getSelection: () => createEditorSelection(true),
            });
            analytics.agentCopilotTextInsertBelowClicked({
                key: 'translate',
            });
            dropdownContext.closeDropdown();
        }
    }, [
        dropdownContext,
        editorContext.content,
        editorContext.selection,
        editorContext.window.document,
        createEditorSelection,
        translatedText,
    ]);
    /**
     * Replace the editor selection with the translated text. If nothing is
     * selected, all content of the editor will be replaced.
     */
    const replace = useCallback(() => {
        const range = editorContext.selection.getRange();
        if (range && translatedText) {
            editorContext.content.insert(
            // eslint-disable-next-line no-unsanitized/method
            range.createContextualFragment(sanitizeHtml(translatedText, sanitizationProfile)), {
                getSelection: () => createEditorSelection(false),
            });
            analytics.agentCopilotTextReplaceClicked({
                key: 'translate',
            });
            dropdownContext.closeDropdown();
        }
    }, [
        dropdownContext,
        editorContext.content,
        editorContext.selection,
        createEditorSelection,
        translatedText,
    ]);
    const isEmpty = useMemo(() => {
        return !getSelectedText().trim();
    }, [getSelectedText]);
    const content = useMemo(() => {
        if (isEmpty) {
            return (_jsx(Banner, { variant: "warning", description: _jsx(_Fragment, { children: "There is no text to translate." }) }));
        }
        if (asyncStatus === 'uninitialized') {
            return (_jsx(AiBanner, { children: "Choose a language to view the translation preview displayed here." }));
        }
        if (asyncStatus === 'pending') {
            return (_jsxs(FlexBox, Object.assign({ gap: "100", flexDirection: "column" }, { children: [_jsx(Skeleton, { className: styles.skeleton, width: "30%" }), _jsx(Skeleton, { className: styles.skeleton, width: "90%" }), _jsx(Skeleton, { className: styles.skeleton, width: "80%" }), _jsx(Skeleton, { className: styles.skeleton, width: "20%" })] })));
        }
        if (asyncStatus === 'rejected') {
            return (_jsx(Banner, { variant: "fail", description: _jsx(_Fragment, { children: "An error occurred while translating into the selected language." }) }));
        }
        if (translatedText) {
            return (_jsx(SafeInnerHtmlSetter, { element: 'p', profile: sanitizationProfile, content: translatedText }));
        }
    }, [asyncStatus, isEmpty, translatedText]);
    return (_jsxs(Box, Object.assign({ style: { width: 768 } }, { children: [_jsxs(FlexBox, Object.assign({ gap: "200", padding: "250" }, { children: [_jsx(FlexBox, Object.assign({ flex: "0 1 100px", alignItems: "center" }, { children: _jsx(Text.Heading, Object.assign({ variant: "heading-3" }, { children: "Translate into" })) })), _jsx(Box, Object.assign({ flex: "1" }, { children: _jsx(CopilotLanguageInputSelectionList, { onLanguageChange: handleLanguageChange, selectedLanguageCode: selectedLanguage === null || selectedLanguage === void 0 ? void 0 : selectedLanguage.code, disabled: isEmpty }) }))] })), _jsx(Divider, {}), _jsx(Box, Object.assign({ padding: "250", className: classnames({
                    [styles.hasTranslatedText]: !!translatedText,
                }) }, { children: content })), _jsx(Divider, {}), _jsxs(FlexBox, Object.assign({ padding: "250" }, { children: [_jsx(Box, { flex: "1" }), _jsxs(FlexBox, Object.assign({ flex: "0", gap: "200" }, { children: [_jsx(Button, Object.assign({ disabled: !translatedText, variant: "secondary", onClick: insertBelow }, { children: "Insert below" })), _jsx(Button, Object.assign({ disabled: !translatedText, variant: "primary", onClick: replace }, { children: "Replace with translation" }))] }))] }))] })));
});
